import { useEffect, useState } from "react";
import { AlertCircle, Loader, Pause, RefreshCw, ShieldCheck } from "lucide-react";
import { getMaintenanceControl, getVersionPolicy } from "../lib/api";
import { summarizeAppAccess } from "../lib/appAccess";
import { PageHeading } from "./PageHeading";
import { MaintenancePanel } from "./MaintenancePanel";
import { VersionPolicyPanel } from "./VersionPolicyPanel";
import type { MaintenanceControl, VersionPolicyControl, Viewer } from "../types";
import { DEPLOYMENT_NAME, EXPECTED_FIREBASE_PROJECT } from "../lib/runtime";

const SUMMARY_ICON = { healthy: ShieldCheck, critical: Pause, unknown: Loader } as const;

export function ControlsPage({ viewer }: { viewer: Viewer }) {
  const [control, setControl] = useState<MaintenanceControl | null>(null);
  const [policy, setPolicy] = useState<VersionPolicyControl | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    const [maintenance, versions] = await Promise.allSettled([getMaintenanceControl(), getVersionPolicy()]);
    if (maintenance.status === "fulfilled") setControl(maintenance.value);
    if (versions.status === "fulfilled") setPolicy(versions.value);
    const failures = [
      maintenance.status === "rejected" ? "the pause setting" : null,
      versions.status === "rejected" ? "the version list" : null
    ].filter((value): value is string => value !== null);
    setError(failures.length ? `Could not load ${failures.join(" or ")}. The values below may be out of date.` : null);
    setLoading(false);
  };

  useEffect(() => { void load(); }, []);

  const summary = summarizeAppAccess(control, policy);
  const SummaryIcon = SUMMARY_ICON[summary.tone];

  return <div className="page-stack">
    <PageHeading
      eyebrow="APP CONTROLS"
      title="App controls"
      description="Two settings decide who can open the development app: whether it is paused, and which app versions are allowed in."
      info={{
        label: "control scope",
        children: <><strong>{DEPLOYMENT_NAME} environment.</strong> These settings apply to {EXPECTED_FIREBASE_PROJECT} and its Realtime Database. Every change needs super-admin access, a fresh Google sign-in, a reason, and a typed confirmation, and is recorded in the audit log.</>
      }}
      actions={<button className="secondary-button" onClick={() => void load()} disabled={loading}>
        <RefreshCw size={16} className={loading ? "spin" : undefined} aria-hidden="true" />Refresh
      </button>}
    />
    {error ? <div className="error-panel"><AlertCircle size={19} aria-hidden="true" /><div><strong>App controls partly unavailable</strong><p>{error}</p></div></div> : null}
    <section className={`access-summary tone-${summary.tone}`} aria-live="polite">
      <span className="access-summary-icon"><SummaryIcon size={24} className={summary.tone === "unknown" ? "spin" : undefined} aria-hidden="true" /></span>
      <div>
        <p className="section-label">RIGHT NOW</p>
        <h2>{summary.headline}</h2>
        <p>{summary.detail}</p>
      </div>
    </section>
    <div className="control-panels">
      <MaintenancePanel viewer={viewer} control={control} loading={loading} onChange={setControl} />
      <VersionPolicyPanel viewer={viewer} policy={policy} loading={loading} onChange={setPolicy} />
    </div>
  </div>;
}
